/**
 * The storefront "Powered by" mark, resolved once for every surface.
 *
 * Card: https://trello.com/c/mq26K91V -- white-label storefront branding
 * (tier-gated). This is the RENDER half; the entitlement half lives in
 * ``api/src/services/white_label.py``.
 *
 * Jake, 2026-09-18/19:
 *
 *     "We need the ability to enable a distributor to change the branding
 *     of their storefronts from ThreadFlow to a brand associated with
 *     themselves or the store they're building."
 *
 * THE RULE (REVISED 2026-09-20 -- Unit 8, and it REPLACES the shipped one)
 * ------------------------------------------------------------------------
 *
 * REVISED AGAIN 2026-09-20 (Unit 10) -- the mark can be a LINK again,
 * to the DISTRIBUTOR'S OWN site. See the note under the table.
 *
 *   toggle  store logo  link URL  renders
 *   ------  ----------  --------  -------------------------------------
 *   off     anything    anything  "Powered by ThreadFlow" + the TF
 *                                 favicon, linked to threadflow.com --
 *                                 byte-identical to what every
 *                                 storefront shows today
 *   on      present     set       the MARK field with ``{logo}``
 *                                 substituted, anchored to the store's
 *                                 URL, opening in a new tab
 *   on      present     empty     the same mark, NOT a link
 *   on      absent      set       the PLAIN-TEXT attribution, anchored
 *                                 to the store's URL, new tab
 *   on      absent      empty     the PLAIN-TEXT attribution, not a link
 *
 * UNIT 10 REVERSES UNIT 3'S ANCHOR DROP, AND ONLY BECAUSE THE DESTINATION
 * CHANGED. Unit 3 removed the anchor from a white-labeled mark for a
 * sound reason -- the only href this module could emit was
 * ``threadflow.com``, and a distributor's own logo linking to ThreadFlow's
 * marketing site is the leak white-labeling exists to prevent. Jake is not
 * undoing that reasoning; he is supplying the piece it was missing
 * (2026-09-20):
 *
 *     "The 'Powered by {logo}' footer should be a link to open in a new
 *     tab/window. We need a field on the branding page in the store editor
 *     under White-label branding for entering the URL."
 *
 * So the anchor returns only when there is somewhere of the distributor's
 * OWN to point it at. The link field is OPTIONAL, and an empty one renders
 * Unit 3's unlinked mark unchanged -- never an empty or "#" anchor, which
 * would look clickable and do nothing.
 *
 * WHY THE OLD RULE IS GONE
 * ------------------------
 *
 * The shipped rule 3 said "enabled + no logo -> show the ThreadFlow logo",
 * so that flipping a tier was safe before anyone uploaded anything. Jake's
 * first live test on 8083 killed it, verbatim (2026-09-20):
 *
 *     "if no logo, then use the plain-text attribution, and if there's a
 *     logo use the text with the placeholder -- two fields, one plain text
 *     and one with the logo. If the toggle is on, no ThreadFlow logo at
 *     all. If the toggle is off, ThreadFlow attribution with logo."
 *
 * "If the toggle is on, no ThreadFlow logo at all" is the load-bearing
 * sentence. The ordering-safety the old rule bought is now bought by the
 * TOGGLE instead: a tier can be enabled for a distributor who has uploaded
 * nothing, and nothing changes on their storefronts until the store's own
 * toggle goes on. So the guarantee survives; it just moved to a control
 * the distributor operates, which is the point -- the old rule made
 * ThreadFlow's mark appear on a store that had asked for white-labeling,
 * which is the one outcome the feature exists to prevent.
 *
 * TWO FIELDS, BECAUSE A LOGO CAN BE A WORDMARK
 * --------------------------------------------
 *
 * What Jake actually saw was "Powered by GTC [GTC-LOGO]" -- the brand
 * named twice, because the logo IS the word. Shortening the single field
 * to "Powered by" fixed the storefront and broke every surface that cannot
 * render an image. Hence:
 *
 *   - ``attribution``: plain text, no placeholder. "Powered by GTC". The
 *     form used by email plain-text parts, ``alt`` text, and the no-logo
 *     row above -- anywhere an image has nowhere to go.
 *   - ``mark``: carries ``{logo}``. "Powered by {logo}". Used only where
 *     an image CAN render. OPTIONAL: Jake, "Logo alone if there is one,
 *     text-only attribution if there isn't a logo."
 *
 * TWO GATES, BOTH REQUIRED
 * ------------------------
 *
 * ``enabled`` is the TIER entitlement, bought once per distributor and
 * still authoritative. ``white_label_on`` is the per-STORE switch WITHIN
 * an entitled tier. A store cannot white-label by flipping its own toggle,
 * and an entitled store does not white-label until it does. Both must be
 * true; either being false renders row 1.
 *
 * WHY ONE HELPER AND NOT THREE COPIES
 * -----------------------------------
 *
 * Three components render this mark: the storefront page footer, the cart
 * footer, and the buyer-dashboard footer. The backend keeps the tier/asset
 * decision in ONE resolver precisely so the footer and the confirmation
 * email cannot disagree; this is that argument one layer out. A store
 * whose cart says "Powered by TSG" while its product page still says
 * "Powered by ThreadFlow" is a half-applied white-label, which is worse
 * than none -- it advertises that the branding is a veneer.
 *
 * SECURITY (CWE-79): WHY SEGMENTS, NOT AN INTERPOLATED STRING
 * -----------------------------------------------------------
 *
 * ``{logo}`` puts an IMAGE in the middle of distributor-authored text. The
 * obvious implementation -- replace the token with an ``<img>`` string and
 * hand back HTML -- would mean building markup out of authored text and
 * binding it with ``v-html``. That is exactly the bug the email path
 * already had and fixed by escaping AT CONSTRUCTION (``mail.py:262``), and
 * re-introducing it here would undo that lesson on a shopper-facing page.
 *
 * So this module returns ORDERED SEGMENTS instead:
 *
 *     [{type: 'text', value: 'Powered by '}, {type: 'logo', src: '...'}]
 *
 * Consumers render ``text`` segments as TEXT nodes (``{{ }}``) and ``logo``
 * segments as ``<img>`` ELEMENTS. There is no code path in which authored
 * text becomes markup, so the vulnerability is structurally ABSENT rather
 * than escaped away -- a future consumer cannot re-open it by forgetting
 * to escape, because it never receives a string to escape.
 *
 * Text is returned VERBATIM and deliberately NOT sanitized here:
 * sanitizing would imply the value is sometimes safe to inject as markup,
 * which is the assumption that produces the bug. Unit 1 validates length
 * and control characters at the write boundary; the render boundary
 * escapes.
 *
 * ``logo_key`` becomes an ``<img src>``, so it is constrained to a plain
 * upload key (see ``resolveLogoSrc``).
 */
import { buildImageUrl } from '@/utils/imageBaseUrl'
import { sanitizeExternalUrl } from '@/utils/sanitizeExternalUrl'

/** The wording shown whenever the store is not white-labeling. */
export const THREADFLOW_ATTRIBUTION = 'Powered by ThreadFlow'

/** The ThreadFlow mark. Served from the SPA's own static assets. */
export const THREADFLOW_LOGO_SRC = '/static/images/redOWfavicon.png'

/**
 * The href for the NON-white-labeled mark. Hard-coded, never user input.
 *
 * REVISED 2026-09-20 (Unit 10). This used to read "the only href this
 * module can emit", and that is no longer true: a white-labeled store can
 * now anchor its mark to the distributor's OWN url. The claim mattered --
 * a reader trusting it would conclude no sanitization could possibly be
 * needed here -- so it is corrected rather than left standing.
 *
 * The rule now:
 *
 *   - NOT white-labeling  -> this constant, always. Unchanged.
 *   - white-labeling      -> the store's `link_url`, and ONLY after
 *                            `sanitizeExternalUrl` accepts it; otherwise
 *                            no anchor at all.
 *
 * A white-labeled store can never emit this constant. That was Unit 3's
 * whole point and it still holds: a distributor's own logo linking to
 * ThreadFlow's marketing site is the leak the feature exists to prevent.
 */
export const THREADFLOW_URL = 'https://threadflow.com/'

/**
 * The token the distributor types to position their logo in the mark.
 *
 * A literal, not a regex, and substituted on the FIRST occurrence only: a
 * mark carrying the token twice would render the same logo twice, which is
 * the doubled-brand look this whole unit exists to remove.
 */
export const LOGO_PLACEHOLDER = '{logo}'

/**
 * The cap on the rendered mark logo, in CSS pixels. Height only.
 *
 * Jake, 2026-09-20: "This would accommodate images smaller than 25px high,
 * and also allow larger images without shrinking them to the point of
 * illegibility." Width is deliberately UNCAPPED -- he considered and
 * declined it as an edge case, so a very wide wordmark renders wide rather
 * than being letterboxed.
 *
 * Exported so the three consuming surfaces cannot each pick their own
 * number; the previous ``height="25" width="25"`` was copied into all
 * three and is what squashed every non-square logo.
 */
export const MARK_LOGO_MAX_HEIGHT_PX = 25

/**
 * Resolve a stored upload key to an image URL, or null when it is not one.
 *
 * The column holds an S3/MinIO object key. Anything carrying a URI SCHEME
 * is not a key, and is refused rather than rendered:
 *
 *   - a full external URL would make the storefront beacon to a third
 *     party on every page view,
 *   - a ``data:`` payload is an injection surface with no legitimate use
 *     here,
 *   - and the legacy seeder placeholder ``stub://image/...`` would paint a
 *     guaranteed-404 broken image.
 *
 * Mirrors ``useStorefrontChrome``'s ``resolveImage``, which already refuses
 * these shapes for the topbar logo -- the same key must not render as a
 * logo on one surface and a broken image on another.
 *
 * MATCHED ON THE SCHEME, NOT ON ``://``, and that difference is the whole
 * point: ``resolveImage`` tests for ``://``, which ``javascript:alert(1)``
 * does not contain -- it has no authority component. That value would sail
 * through a ``://`` check and be concatenated onto the image base URL. It
 * cannot execute from an ``<img src>``, so this is defence in depth rather
 * than a live hole, but "the key looked safe because it was malformed" is
 * not a property to rely on. The RFC 3986 scheme grammar
 * (ALPHA *( ALPHA / DIGIT / "+" / "-" / "." ) ":") catches both shapes.
 * A bare key -- a uuid, or ``seed-<sha>.jpg`` -- has no colon at all.
 */
const URI_SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i

function resolveLogoSrc(logoKey) {
    if (!logoKey) return null
    if (URI_SCHEME_RE.test(String(logoKey))) return null
    return buildImageUrl(logoKey)
}

/**
 * Resolve the distributor's brand link to a safe href, or null (Unit 10).
 *
 * SECURITY (CWE-79 / CWE-601): this is the ONE white-label value that
 * becomes an ``href``, which makes it the one where the rest of this
 * module's defense does not apply. Everywhere else the answer is "render
 * it as a TEXT node and markup cannot form"; an href has no such escape
 * hatch, because an escaped ``javascript:`` URL is still a ``javascript:``
 * URL when the browser follows the link. The scheme has to be REFUSED.
 *
 * WHY ``sanitizeExternalUrl`` AND NOT A NEW CHECK HERE. The storefront
 * footer that renders this mark ALREADY applies exactly that helper to the
 * distributor-authored Privacy Policy and Refund Policy links sitting
 * beside it (StorefrontFooter.vue). A second, subtly-different rule in the
 * same footer is how two links end up with two different notions of safe.
 *
 * WHY NOT ``sanitizeStorefrontUrl``, the other one. That helper
 * deliberately ALLOWS same-site relative paths ("/...") because a hero
 * slide legitimately links INTO the store. A brand attribution points at
 * the distributor's OWN external site, so admitting a relative path would
 * let the white-label mark link back into the storefront it sits on.
 * ``sanitizeExternalUrl`` requires an absolute http(s) URL, which is the
 * only shape that means anything here.
 *
 * A REFUSAL RETURNS null, WHICH RENDERS NO ANCHOR -- never an anchor with
 * an emptied href. A dead ``<a>`` around the mark looks clickable, does
 * nothing, and gives a shopper no way to tell it from a broken site. The
 * unlinked mark is a perfectly good render (it is what Unit 3 shipped), so
 * degrading to it costs nothing.
 *
 * Note the backend validates this at the WRITE boundary too
 * (``_normalize_white_label_link_url``). Neither gate is redundant: a row
 * can predate a validator, and client-side sanitizing alone is not a
 * boundary because ``update_campaign`` is a directly reachable RPC.
 */
function resolveLinkHref(linkUrl) {
    return sanitizeExternalUrl(linkUrl)
}

/**
 * True when a field holds authored content rather than nothing.
 *
 * Trimmed because whitespace is not authorship: a field someone opened and
 * left blank must read as unset, not as an empty line under the logo. The
 * value itself is NOT trimmed for rendering -- only tested -- since the
 * author's spacing is theirs.
 */
function isAuthored(value) {
    return typeof value === 'string' && value.trim() !== ''
}

/** A text segment, dropped entirely when it would be empty. */
function textSegments(value) {
    return value === '' ? [] : [{ type: 'text', value }]
}

/**
 * Split an authored mark around the FIRST ``{logo}`` into render segments.
 *
 * An author who typed words but no token still gets their logo -- it is
 * appended. Dropping it would look like the upload failed, and there is no
 * reading of "upload a logo, write a mark" in which the logo should vanish
 * because the token was forgotten.
 */
function markSegments(markText, logoSrc) {
    const logo = { type: 'logo', src: logoSrc }

    if (!isAuthored(markText)) {
        // Jake: "Logo alone if there is one." The placeholder field is
        // optional and a bare logo is the intended render.
        return [logo]
    }

    const at = markText.indexOf(LOGO_PLACEHOLDER)
    if (at === -1) {
        return [...textSegments(markText), logo]
    }

    return [
        ...textSegments(markText.slice(0, at)),
        logo,
        ...textSegments(markText.slice(at + LOGO_PLACEHOLDER.length)),
    ]
}

/**
 * Is this store white-labeling? BOTH gates, in one place.
 *
 * ``enabled`` is the TIER entitlement, bought once per distributor.
 * ``white_label_on`` is the per-STORE switch WITHIN an entitled tier. Both
 * must be true: a store cannot white-label by flipping its own toggle, and
 * an entitled store does not white-label until it does.
 *
 * Real boolean tests, not truthiness: a mis-serialized ``"false"`` string is
 * truthy in JS, and deny is the floor for both gates. An absent key -- a
 * payload predating this feature, a mock, or a fetch that has not landed --
 * reads as NOT white-labeling, so "no answer yet" and "not entitled" look
 * the same, which is the correct-looking state either way.
 *
 * WHY THIS IS EXPORTED, and it is not a convenience (2026-09-20, card
 * mq26K91V). Consumers outside the mark itself need this answer: the global
 * version/status bar in ``App.vue`` drops the word "ThreadFlow" when a
 * white-labeled storefront is on screen. Its first implementation inferred
 * the answer from the RENDER SHAPE -- ``!mark.isLink``, which was sound when
 * the only href the resolver could emit was threadflow.com.
 *
 * UNIT 10 BROKE THAT INFERENCE WITHIN THE HOUR: a white-labeled store with
 * ``link_url`` set now also returns ``isLink: true``, so ``!isLink`` would
 * have reported "not white-labeled" for exactly the stores the feature is
 * for, putting ThreadFlow's name back on a white-labeled page. The unit
 * tests did not catch it because their fixture never set ``link_url``.
 *
 * The lesson is the reason this function exists: a field describing HOW to
 * render is not a stable proxy for WHETHER the feature is on, because the
 * render can legitimately change. Ask the question you mean.
 *
 * @param {{enabled: ?boolean, white_label_on: ?boolean}} whiteLabel
 *   The ``white_label`` object off the storefront campaign payload.
 *   Null/undefined is tolerated and reads as false.
 * @returns {boolean}
 */
export function isWhiteLabelActive(whiteLabel) {
    const wl = whiteLabel || {}
    return wl.enabled === true && wl.white_label_on === true
}

/**
 * The mark to render for one storefront.
 *
 * @param {{enabled: boolean, white_label_on: boolean, logo_key: ?string,
 *          attribution: ?string, mark: ?string, link_url: ?string}}
 *          whiteLabel
 *   The ``white_label`` object from ``GET /campaign/getStorefrontCampaign``
 *   (or ``getCampaignById``). Missing/undefined is tolerated -- see below.
 * @returns {{segments: Array<{type: string, value?: string, src?: string}>,
 *            text: string, altText: string, isLink: boolean, href: ?string,
 *            isStoreLogo: boolean, logoMaxHeightPx: number}}
 *   ``segments`` is the render order: bind ``text`` segments as TEXT and
 *   ``logo`` segments as ``<img>``. ``text`` is the flat form of what the
 *   mark actually SHOWS; ``altText`` is the plain-text attribution, which
 *   is what an ``alt`` needs when the mark is a bare wordmark logo with no
 *   words of its own.
 */
export function resolveWhiteLabelMark(whiteLabel) {
    const wl = whiteLabel || {}

    if (!isWhiteLabelActive(wl)) {
        return {
            segments: [
                { type: 'text', value: THREADFLOW_ATTRIBUTION },
                { type: 'logo', src: THREADFLOW_LOGO_SRC },
            ],
            text: THREADFLOW_ATTRIBUTION,
            altText: THREADFLOW_ATTRIBUTION,
            isLink: true,
            href: THREADFLOW_URL,
            isStoreLogo: false,
            logoMaxHeightPx: MARK_LOGO_MAX_HEIGHT_PX,
        }
    }

    const storeLogoSrc = resolveLogoSrc(wl.logo_key)
    const isStoreLogo = storeLogoSrc !== null

    // The distributor's OWN destination (Unit 10). Resolved once, above
    // both white-labeled rows, because BOTH of them carry it: a store
    // with a logo and a store with only text are the same brand and link
    // to the same place. Applying it to one row only is the shape of bug
    // that ships -- the tested row clean, the other one live.
    //
    // null here means "no link", which renders exactly what Unit 3
    // shipped. That covers an unset field AND a refused one, so a
    // dangerous URL degrades to a good render rather than a broken
    // anchor.
    const linkHref = resolveLinkHref(wl.link_url)
    const isLink = linkHref !== null

    // WHITE-LABELED, NO LOGO: the plain-text field and NO icon. This is
    // the row that reverses the shipped behavior -- there is deliberately
    // no ThreadFlow fallback here, because the toggle being on means the
    // distributor has asked for no ThreadFlow branding anywhere. A store
    // with neither asset set renders an empty mark, which is correct: they
    // asked for none of ours and supplied none of their own.
    //
    // Note a refused logo_key (an external URL, a stub:// placeholder)
    // lands here too, which is why the refusal degrades to text rather
    // than to a broken image.
    if (!isStoreLogo) {
        const text = isAuthored(wl.attribution) ? wl.attribution : ''
        return {
            segments: textSegments(text),
            text,
            altText: text,
            // Unit 10: the plain-text attribution is anchored too when the
            // distributor supplied a URL. It is their brand either way --
            // the presence of a logo is not what makes the mark worth
            // linking.
            isLink,
            href: linkHref,
            isStoreLogo: false,
            logoMaxHeightPx: MARK_LOGO_MAX_HEIGHT_PX,
        }
    }

    // WHITE-LABELED WITH A LOGO: the placeholder field, substituted.
    const segments = markSegments(wl.mark, storeLogoSrc)

    return {
        segments,
        // The flat text form, for `alt` and any non-visual consumer. It is
        // the MARK's words, not the plain attribution: those two differ on
        // purpose (one names the brand, one defers to the image), and an
        // `alt` describing the image should read as the mark does.
        text: segments
            .filter((s) => s.type === 'text')
            .map((s) => s.value)
            .join(''),
        // The PLAIN-TEXT attribution, for an `alt` when the mark has no
        // words of its own. A bare wordmark logo is not decorative -- it
        // IS the attribution -- so an empty alt would be wrong, and the
        // plain field is exactly the string written for surfaces that
        // cannot show the image.
        altText: isAuthored(wl.attribution) ? wl.attribution : '',
        // REVISED 2026-09-20 (Unit 10). Unit 3 dropped the anchor here
        // unconditionally, and its reason was right: the only href
        // available was threadflow.com, and a distributor's own logo
        // linking to ThreadFlow's marketing site is precisely the leak
        // this feature exists to prevent. That reason is unchanged -- what
        // changed is that there is now a DIFFERENT destination available,
        // the distributor's own. With `link_url` unset or refused this is
        // still null and the anchor still drops, exactly as Unit 3 left
        // it. What can never happen is this row emitting THREADFLOW_URL.
        isLink,
        href: linkHref,
        isStoreLogo: true,
        logoMaxHeightPx: MARK_LOGO_MAX_HEIGHT_PX,
    }
}

export default resolveWhiteLabelMark
